// useTheme — the single, documented light/dark switch for the app (FE-00).
//
// Dark mode is entirely a token swap: this composable only flips
// `documentElement.dataset.theme` between 'light' and 'dark'; styles/tokens.css
// re-points every `--…` variable off that attribute and the `@theme inline`
// utilities follow. No component uses Tailwind's `dark:` variant.
//
// Resolution order on boot (see `initTheme`):
//   1. A runtime-config `theme.dataTheme` — applied by stores/config.ts
//      before mount, so it is already on <html> here and WINS.
//   2. The user's last explicit choice, persisted to localStorage.
//   3. The OS `prefers-color-scheme`.
//
// State is module-scoped so every call site shares one reactive source.
import { computed, readonly, ref, type ComputedRef, type DeepReadonly, type Ref } from 'vue';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'dc-theme';

const theme = ref<Theme>('light');
let initialised = false;

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    // Private-mode / disabled storage — fall through to OS preference.
    return null;
  }
}

/** Write the resolved theme to <html> and the reactive ref. Persistence is
 * opt-in (skipped when we merely mirror a runtime-config/OS default). */
function applyTheme(next: Theme, persist: boolean): void {
  theme.value = next;
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = next;
  }
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Non-fatal: the choice still applies for this session.
    }
  }
}

/** Resolve and apply the boot theme. Idempotent — safe to call once from the
 * app root even if primitives also `useTheme()`. */
function initTheme(): void {
  if (initialised) return;
  initialised = true;

  const fromConfig =
    typeof document !== 'undefined' ? document.documentElement.dataset.theme : undefined;
  if (fromConfig === 'light' || fromConfig === 'dark') {
    // Runtime-config already set it before mount — honour it, don't persist
    // (config, not the user, is the authority this boot).
    theme.value = fromConfig;
    return;
  }

  const resolved: Theme = readStored() ?? (systemPrefersDark() ? 'dark' : 'light');
  applyTheme(resolved, false);
}

export interface UseTheme {
  theme: DeepReadonly<Ref<Theme>>;
  isDark: ComputedRef<boolean>;
  setTheme: (next: Theme) => void;
  toggleTheme: () => void;
  initTheme: () => void;
}

export function useTheme(): UseTheme {
  return {
    theme: readonly(theme),
    isDark: computed(() => theme.value === 'dark'),
    // An explicit user choice always persists.
    setTheme: (next: Theme) => applyTheme(next, true),
    toggleTheme: () => applyTheme(theme.value === 'dark' ? 'light' : 'dark', true),
    initTheme,
  };
}

/** Test-only: reset the module singleton between specs. */
export function __resetThemeForTests(): void {
  initialised = false;
  theme.value = 'light';
}
